import { FeedbackCard } from '@/@studio-ai/components/history/feedback-card';
import { FeedbackTabProps } from '@/@studio-ai/props';
import { tracking } from '@/lib/features/tracking';
import { Button, Chip, cn, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { UserFeedbackFilter } from '../../constants';
import type { UserFeedback } from '../../types';

const FILTER_OPTIONS: { key: UserFeedbackFilter; label: string; icon: string }[] = [
    { key: UserFeedbackFilter.ALL, label: 'All', icon: 'lucide:list' },
    { key: UserFeedbackFilter.UNREAD, label: 'Unread', icon: 'lucide:bell-dot' },
    { key: UserFeedbackFilter.HAS_RESPONSE, label: 'Responded', icon: 'lucide:message-circle' },
    { key: UserFeedbackFilter.NO_RESPONSE, label: 'Pending', icon: 'lucide:clock' },
    { key: UserFeedbackFilter.READ, label: 'Read', icon: 'lucide:check-circle' },
];

export function FeedbackTab({
    userFeedback,
    userFeedbackLoading,
    userFeedbackFilter,
    setUserFeedbackFilter,
    markFeedbackAsRead,
    refetchUserFeedback,
    openConversation,
    unreadFeedbackCount,
}: FeedbackTabProps) {
    const [expandedFeedback, setExpandedFeedback] = useState<number | null>(null);
    const [expandedAiMessage, setExpandedAiMessage] = useState<number | null>(null);

    const handleMarkAsRead = async (feedbackId: number) => {
        try {
            await markFeedbackAsRead(feedbackId);
            await refetchUserFeedback();
            toast.success('Marked as read', { position: 'top-right', autoClose: 2000 });
        } catch (error) {
            tracking.errorWithSource(error as Error, 'FeedbackTab.handleMarkAsRead', {
                feedbackId,
            });
            toast.error('Failed to mark as read', { position: 'top-right', autoClose: 3000 });
        }
    };

    const getEmptyStateMessage = () => {
        switch (userFeedbackFilter) {
            case UserFeedbackFilter.UNREAD:
                return 'No new responses to read';
            case UserFeedbackFilter.HAS_RESPONSE:
                return 'No feedback with responses yet';
            case UserFeedbackFilter.NO_RESPONSE:
                return 'All feedback has been responded to';
            case UserFeedbackFilter.READ:
                return 'No read responses yet';
            default:
                return 'No feedback submitted yet';
        }
    };

    if (userFeedbackLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <Spinner size="lg" color="primary" />
                <p className="mt-4 text-small text-default-400">Loading feedback...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Filter pills */}
            <div className="flex flex-wrap items-center gap-2">
                {FILTER_OPTIONS.map((opt) => {
                    const isActive = userFeedbackFilter === opt.key;
                    return (
                        <Button
                            key={opt.key}
                            size="sm"
                            variant={isActive ? 'flat' : 'light'}
                            color={isActive ? 'primary' : 'default'}
                            onPress={() => setUserFeedbackFilter(opt.key)}
                            startContent={<Icon icon={opt.icon} width={14} />}
                            className={cn(
                                'h-8 text-small font-medium transition-all',
                                isActive
                                    ? 'bg-primary-100 text-primary-700'
                                    : 'text-default-500 hover:text-default-700',
                            )}
                        >
                            {opt.label}
                            {opt.key === UserFeedbackFilter.UNREAD && unreadFeedbackCount > 0 && (
                                <Chip
                                    size="sm"
                                    color="danger"
                                    variant="solid"
                                    className="ml-1 h-4 min-w-4 px-1 text-[10px]"
                                >
                                    {unreadFeedbackCount}
                                </Chip>
                            )}
                        </Button>
                    );
                })}
            </div>

            {/* Content */}
            {!userFeedback || userFeedback.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-default-100">
                        <Icon
                            icon="lucide:message-square-quote"
                            width={28}
                            className="text-default-300"
                        />
                    </div>
                    <p className="mt-4 text-base font-medium text-default-600">
                        {getEmptyStateMessage()}
                    </p>
                    <p className="mt-1 text-small text-default-400">
                        Your feedback responses from the team will appear here
                    </p>
                </div>
            ) : (
                <div className="divide-y divide-default-200 rounded-xl border border-default-200">
                    {userFeedback.map((fb: UserFeedback) => (
                        <FeedbackCard
                            key={fb.id}
                            feedback={fb}
                            expandedFeedback={expandedFeedback}
                            expandedAiMessage={expandedAiMessage}
                            onToggleFeedback={(id) =>
                                setExpandedFeedback(expandedFeedback === id ? null : id)
                            }
                            onToggleAiMessage={(id) =>
                                setExpandedAiMessage(expandedAiMessage === id ? null : id)
                            }
                            onMarkAsRead={handleMarkAsRead}
                            onOpenConversation={openConversation}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
